import React, { useState } from "react";
import Label from "./fromLabel";
import Error from "./fromError";
import Icon from "../../icons";
import toast from "react-hot-toast";

interface DecimalInputProps {
  value?: string | number;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  min?: number;
  max?: number;
  step?: number;
  decimalPlaces?: number;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  clearError?: () => void;
  name?: string;
  fieldType?: "unit" | "amount";
  labelSpaceNeeded?: boolean; // New prop to control label space
  withDollarIcon?: boolean;
  maxErrorMessage?: string;
}

const DecimalInput: React.FC<DecimalInputProps> = ({
  value = "",
  onChange,
  onBlur,
  placeholder = "",
  className = "",
  min = 0,
  max = 100,
  step = 0.01,
  decimalPlaces = 2,
  error,
  disabled = false,
  required = false,
  label,
  clearError,
  name,
  fieldType = "amount",
  withDollarIcon = false,
  labelSpaceNeeded = false,
  maxErrorMessage,
  ...props
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;
    
    // For unit type, only allow integers (no decimal point)
    if (fieldType === "unit") {
      if (!/^[0-9]*$/.test(inputValue)) {
        return;
      }
    } else {
      // For amount type, allow numbers and decimal point
      if (!/^[0-9]*\.?[0-9]*$/.test(inputValue)) {
        return;
      }
    }

    // Clear the error when user starts typing
    if (error && clearError) {
      clearError();
    }

    // If empty, set to min value immediately
    if (inputValue === "") {
      onChange?.(min.toString());
      return;
    }

    // Prevent negative values
    if (inputValue.startsWith("-")) {
      return;
    }

    // Remove leading zeros (except for decimal values like 0.5)
    if (
      inputValue.length > 1 &&
      inputValue.startsWith("0") &&
      !inputValue.startsWith("0.")
    ) {
      inputValue = inputValue.replace(/^0+/, "") || "0";
    }

    // Check if value exceeds max
    const numValue = parseFloat(inputValue);
    if (!isNaN(numValue) && numValue > max) {
      toast.error(maxErrorMessage ?? `Value cannot exceed maximum of ${max}`);
      return;
    }

    // Check if value is below min (only for completed values)
    if (!isNaN(numValue) && numValue < min && !inputValue.endsWith(".")) {
      // Allow typing values that will eventually be valid (like typing "1" when min is 10)
      // Only enforce min on blur or when the value is complete
    }

    // Check decimal places (only for amount type)
    if (fieldType === "amount" && inputValue.includes(".")) {
      const parts = inputValue.split(".");
      if (parts[1] && parts[1].length > decimalPlaces) {
        return; // Don't update if more than allowed decimal places
      }
    }

    onChange?.(inputValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace when field would become empty
    if (e.key === "Backspace") {
      const currentValue = (e.target as HTMLInputElement).value;
      
      // If backspacing would make it empty (single character remaining)
      if (currentValue.length <= 1) {
        e.preventDefault();
        onChange?.(min.toString());
        return;
      }
      
      // If value would be less than 1 and is short, reset to min
      if (Number(currentValue) < 1 && currentValue.length <= 3) {
        e.preventDefault();
        onChange?.(min.toString());
        return;
      }
    }

    // Prevent minus, plus, 'e' (scientific notation), and decimal point for unit type
    const preventKeys = ["-", "+", "e", "E"];
    if (fieldType === "unit") {
      preventKeys.push(".");
    }
    
    if (preventKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleBlur = () => {
    let currentValue = value?.toString() || "0";
    
    // Handle incomplete decimal entries (e.g., "0.", "2.", "3.")
    if (currentValue.endsWith(".")) {
      currentValue = currentValue.slice(0, -1); // Remove the trailing dot
      onChange?.(currentValue);
    }
    
    // Enforce min value on blur
    const numValue = parseFloat(currentValue);
    if (!isNaN(numValue) && numValue < min) {
      onChange?.(min.toString());
    }
    
    onBlur?.();
  };

  return (
    <div>
      <div className={`!my-0 ${className}`}>
        {label && <Label text={label} required={required} info={""} isElipsis />}
        <div className="relative">
          {withDollarIcon && (
            <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-[#222222] text-[0.938rem] pointer-events-none${label ? " mt-1" : ""}`}>
              $
            </span>
          )}
          <input
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            type="text"
            placeholder={placeholder}
            value={value != undefined ? value : ""}
            onChange={handleChange}
            disabled={disabled}
            min={min}
            step={step}
            name={name}
            className={`${
              error ? "border-red-500 border-2" : "border-[#CECECE] border"
            } rounded-md focus:outline-none focus:primary text-[0.938rem] ${
              className.includes("mt-") ? "" : label ? "mt-2" : labelSpaceNeeded ? "mt-2" : ""
            } ${withDollarIcon ? "pl-7" : "pl-4"} py-2 font-normal h-[2.875rem] text-[#222222] placeholder:text-[#a1a1a1] w-full`}
          />
        </div>
        {error && <Error message={error} />}
      </div>
    </div>
  );
};

export default DecimalInput;
